/** @typedef {'text' | 'select' | 'date'} DataTableFilterType */

/**
 * @typedef {object} DataTableColumn
 * @property {string} id
 * @property {string} label
 * @property {(row: object) => unknown} [accessor]
 * @property {string} [accessorKey] - shorthand when accessor is row => row[key]
 * @property {DataTableFilterType} [filterType]
 * @property {boolean} [sortable]
 * @property {boolean} [filterable]
 * @property {{ value: string, label: string }[]} [filterOptions]
 * @property {(row: object, filterValue: string) => boolean} [matchFilter]
 * @property {(a: unknown, b: unknown) => number} [sortCompare]
 * @property {(row: object) => import('react').ReactNode} [render] - optional cell renderer when using auto rows
 * @property {string} [cellClassName]
 * @property {boolean} [hideInTable] - use for filter-only columns on filter strips
 */

export function getColumnValue(row, column) {
  if (column.accessor) return column.accessor(row);
  if (column.accessorKey) return row[column.accessorKey];
  return row[column.id];
}

export function formatCellForFilter(value) {
  if (value == null || value === "") return "";
  return String(value);
}

function dateMs(value) {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

function defaultSortCompare(a, b, type) {
  if (type === "date") {
    const av = dateMs(a);
    const bv = dateMs(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return av - bv;
  }
  const av = formatCellForFilter(a).toLowerCase();
  const bv = formatCellForFilter(b).toLowerCase();
  return av.localeCompare(bv);
}

function defaultMatchFilter(row, column, filterValue) {
  if (!filterValue) return true;
  if (column.matchFilter) return column.matchFilter(row, filterValue);
  const raw = formatCellForFilter(getColumnValue(row, column));
  if (column.filterType === "select") return raw === filterValue;
  if (column.filterType === "date") {
    const day = String(filterValue).trim().slice(0, 10);
    if (!day) return true;
    return raw.slice(0, 10) === day || raw.includes(day);
  }
  return raw.toLowerCase().includes(String(filterValue).toLowerCase());
}

/**
 * @param {object[]} data
 * @param {DataTableColumn[]} columns
 * @param {Record<string, string>} columnFilters
 * @param {{ id: string, desc: boolean } | null} sorting
 */
export function applyDataTable(data, columns, columnFilters, sorting) {
  let rows = [...data];
  const colMap = Object.fromEntries(columns.map((c) => [c.id, c]));

  rows = rows.filter((row) =>
    Object.entries(columnFilters).every(([id, value]) => {
      if (!value) return true;
      const col = colMap[id];
      if (!col) return true;
      return defaultMatchFilter(row, col, value);
    }),
  );

  if (sorting?.id) {
    const col = colMap[sorting.id];
    if (col?.sortable !== false) {
      const dir = sorting.desc ? -1 : 1;
      const type = col.filterType === "date" ? "date" : "text";
      rows.sort((a, b) => {
        const cmp = col.sortCompare
          ? col.sortCompare(getColumnValue(a, col), getColumnValue(b, col))
          : defaultSortCompare(getColumnValue(a, col), getColumnValue(b, col), type);
        if (cmp !== 0) return cmp * dir;
        return (Number(a.id) - Number(b.id)) * dir;
      });
    }
  }

  return rows;
}

export function hasActiveColumnFilters(columnFilters) {
  return Object.values(columnFilters).some((v) => v != null && String(v).trim() !== "");
}
